// Stock Take Start Validation Logic
import { StockItem } from "@/types/stock-item";

export interface StockTakeStartValidationResult {
  canProceed: boolean;
  validItems: StockItem[];
  excludedItems: StockItem[];
  warnings: string[];
}

export interface PreparedStockTakeItem {
  code: string;
  description: string;
  category: string;
  location: string;
  expectedQuantity: number;
  unitPrice: number;
  warehouseCode: string;
  locationCode: string;
}

/**
 * Validates items for starting a stock take
 * Only active items can be included in a stock take
 */
export function validateStockTakeStart(items: StockItem[]): StockTakeStartValidationResult {
  const validItems: StockItem[] = [];
  const excludedItems: StockItem[] = [];
  const warnings: string[] = [];

  items.forEach((item) => {
    // Check if item is active
    if (item.inactive) {
      excludedItems.push(item);
    } else {
      validItems.push(item);
    }
  });

  // Generate warnings
  if (excludedItems.length > 0) {
    warnings.push(
      `${excludedItems.length} inactive item${excludedItems.length > 1 ? "s" : ""} will be excluded from the stock take`
    );
  }

  // Check if we can proceed
  const canProceed = validItems.length > 0;

  return {
    canProceed,
    validItems,
    excludedItems,
    warnings,
  };
}

/**
 * Prepares stock items for stock take creation
 * Converts StockItem to the format expected by StockTakeCreatePage
 */
export function prepareStockTakeItems(items: StockItem[]): PreparedStockTakeItem[] {
  return items.map((item) => ({
    code: item.code,
    description: item.description,
    category: item.category || "Uncategorized",
    location: `${item.warehouseCode || ""} - ${item.locationCode || ""}`.trim(),
    expectedQuantity: item.physicalQuantity || 0,
    unitPrice: item.averageCost || item.unitPrice || 0,
    warehouseCode: item.warehouseCode || "",
    locationCode: item.locationCode || "",
  }));
}

/**
 * Validates and prepares items for stock take
 * Returns validation result and prepared items
 */
export function validateAndPrepareStockTake(items: StockItem[]): {
  validation: StockTakeStartValidationResult;
  preparedItems: PreparedStockTakeItem[];
} {
  const validation = validateStockTakeStart(items);
  const preparedItems = prepareStockTakeItems(validation.validItems);

  return {
    validation,
    preparedItems,
  };
}
